import * as React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  subMonths,
  isSameDay,
  isToday,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/app/utils/cn";
import { Button } from "@/view/components/ui/button";
import { Calendar } from "@/view/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/view/components/ui/popover";

interface DateRangePickerProps {
  date?: DateRange;
  onDateChange?: (date: DateRange | undefined) => void;
  className?: string;
  numberOfMonths?: 1 | 2;
}

export function DateRangePicker({
  date,
  onDateChange,
  className,
  numberOfMonths = 2,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [internalDate, setInternalDate] = React.useState<DateRange | undefined>(
    date,
  );

  React.useEffect(() => {
    setInternalDate(date);
  }, [date]);

  const handleDateSelect = (newDate: DateRange | undefined) => {
    if (internalDate?.from && internalDate?.to && newDate?.from) {
      setInternalDate({ from: newDate.from, to: undefined });
      onDateChange?.({ from: newDate.from, to: undefined });
    } else {
      setInternalDate(newDate);
      onDateChange?.(newDate);
    }
  };

  const presets = [
    {
      label: "Semana Atual",
      getValue: () => ({
        from: startOfWeek(new Date(), { weekStartsOn: 0 }),
        to: endOfWeek(new Date(), { weekStartsOn: 0 }),
      }),
    },
    {
      label: "Semana Passada",
      getValue: () => ({
        from: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 0 }),
        to: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 0 }),
      }),
    },
    {
      label: "Mês Atual",
      getValue: () => ({
        from: startOfMonth(new Date()),
        to: endOfMonth(new Date()),
      }),
    },
    {
      label: "Mês Passado",
      getValue: () => ({
        from: startOfMonth(subMonths(new Date(), 1)),
        to: endOfMonth(subMonths(new Date(), 1)),
      }),
    },
  ];

  const handlePresetClick = (preset: (typeof presets)[0]) => {
    const newDate = preset.getValue();
    setInternalDate(newDate);
    onDateChange?.(newDate);
  };

  const handleReset = () => {
    setInternalDate(undefined);
    onDateChange?.(undefined);
  };

  return (
    <div
      className={cn("grid gap-2", className)}
      onClick={(e) => e.stopPropagation()}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-full justify-start text-left font-normal",
              !date && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {internalDate?.from ? (
              internalDate.to ? (
                // Verifica se ambas as datas são hoje
                isSameDay(internalDate.from, internalDate.to) &&
                isToday(internalDate.from) ? (
                  "Hoje"
                ) : (
                  <>
                    {format(internalDate.from, "dd/MM/yyyy", { locale: ptBR })}{" "}
                    - {format(internalDate.to, "dd/MM/yyyy", { locale: ptBR })}
                  </>
                )
              ) : (
                format(internalDate.from, "dd/MM/yyyy", { locale: ptBR })
              )
            ) : (
              <span>Selecione o período</span>
            )}
          </Button>
        </PopoverTrigger>
        {/* <PopoverContent 
          className="w-auto p-0" 
          align="end" 
          side="bottom"
          sideOffset={8}
          alignOffset={-10}
          avoidCollisions={true}
          collisionPadding={16}
        > */}{" "}
        {/* Credliber está usando essas configurações */}
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex">
            {/* Sidebar com presets */}
            <div className="flex flex-col gap-2 border-r p-4">
              <div className="mb-2 text-sm font-medium">Períodos</div>
              {presets.map((preset) => (
                <Button
                  key={preset.label}
                  variant="ghost"
                  className="justify-start rounded-[50px] text-sm"
                  onClick={() => handlePresetClick(preset)}
                >
                  {preset.label}
                </Button>
              ))}
              <div className="my-2 border-t" />
              <Button
                variant="outline"
                className="justify-start rounded-[50px] text-sm"
                onClick={handleReset}
              >
                Resetar
              </Button>
            </div>
            <div className="p-4">
              <Calendar
                mode="range"
                defaultMonth={internalDate?.from}
                selected={internalDate}
                onSelect={handleDateSelect}
                numberOfMonths={numberOfMonths}
                locale={ptBR}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
